// src/pages/public/CoachProfiles.jsx
import React, { useState, useEffect } from "react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import {
  FaUserShield,
  FaSearch,
  FaMapMarkerAlt,
  FaRunning,
  FaTrophy,
  FaMedal,
  FaTimes,
  FaAward,
  FaFilePdf,
  FaExternalLinkAlt
} from "react-icons/fa";

const resolvePhoto = (photoPath) => {
  if (!photoPath) return null;
  return resolveAssetUrl(photoPath);
};

const CoachProfiles = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [coachProfileData, setCoachProfileData] = useState(null);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    setLoading(true);
    try {
      const res = await API.camp.getPublicCoaches();
      setCoaches(res?.data?.coaches || res?.coaches || []);
    } catch (err) {
      console.error("Failed to load coach profiles:", err);
    } finally {
      setLoading(false);
    }
  };

  const openCoachModal = async (coach) => {
    setSelectedCoach(coach);
    setProfileLoading(true);
    setCoachProfileData(null);
    try {
      const res = await API.camp.getPublicCoachProfile(coach.id);
      setCoachProfileData(res?.data?.coach || res?.coach || null);
    } catch (err) {
      console.error("Failed to fetch coach profile:", err);
    } finally {
      setProfileLoading(false);
    }
  };

  const closeCoachModal = () => {
    setSelectedCoach(null);
    setCoachProfileData(null);
  };

  // Districts for filtering
  const districts = Array.from(new Set(coaches.map((c) => c.district).filter(Boolean))).sort();

  // Filtered coaches
  const filteredCoaches = coaches.filter((c) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      c.name?.toLowerCase().includes(q) ||
      c.team_name?.toLowerCase().includes(q) ||
      c.district?.toLowerCase().includes(q) ||
      (c.disciplines || []).some((d) => d?.toLowerCase().includes(q));

    const matchesDistrict = districtFilter === "all" || c.district === districtFilter;
    return matchesSearch && matchesDistrict;
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans">
      <Navbar />

      {/* Hero Header */}
      <section className="relative bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white py-14 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 text-xs font-bold uppercase tracking-wider mb-4">
            <FaUserShield /> Official Coaches Directory
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-3">
            Para Sports Coaching Staff & Mentors
          </h1>
          <p className="text-sm sm:text-base text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Distinguished para coaches and former champion athletes appointed to train, empower, and mentor Andhra Pradesh's divyang athletes to podium success.
          </p>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex-1 w-full space-y-6">
        {/* Search & Filter Bar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <FaSearch className="absolute left-3.5 top-3 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search by coach name or sport..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-indigo-600 focus:bg-white transition"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider whitespace-nowrap">
              District:
            </span>
            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-slate-800 focus:ring-2 focus:ring-indigo-600 focus:bg-white w-full sm:w-auto"
            >
              <option value="all">All Districts ({coaches.length})</option>
              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Coach Cards Grid */}
        {loading ? (
          <div className="py-20 text-center text-slate-400">
            <div className="w-10 h-10 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-semibold">Loading certified coach profiles...</p>
          </div>
        ) : filteredCoaches.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500 shadow-sm">
            <FaUserShield className="text-4xl text-slate-300 mx-auto mb-3" />
            <h3 className="text-base font-bold text-slate-800">No Coaches Found</h3>
            <p className="text-xs text-slate-400 mt-1">Try adjusting your search keywords or district filter.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCoaches.map((coach) => (
              <div
                key={coach.id}
                className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group hover:-translate-y-1"
              >
                {/* Card Top / Photo */}
                <div className="p-6 flex flex-col items-center text-center bg-gradient-to-b from-slate-50 to-white border-b border-slate-100 relative">
                  <div className="relative mb-3">
                    {coach.photo ? (
                      <img
                        src={resolvePhoto(coach.photo)}
                        alt={coach.name}
                        className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-md group-hover:scale-105 transition-transform"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/placeholder-avatar.png";
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-indigo-900 text-white flex items-center justify-center font-black text-2xl shadow-md border-4 border-white">
                        {coach.name?.charAt(0) || "C"}
                      </div>
                    )}
                    <span className="absolute bottom-1 right-1 w-5 h-5 bg-emerald-500 border-2 border-white rounded-full flex items-center justify-center shadow" title="Active Appointed Coach">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                    </span>
                  </div>

                  <h3 className="font-bold text-base text-slate-900 leading-snug group-hover:text-indigo-900 transition-colors">
                    {coach.name}
                  </h3>

                  {coach.team_name ? (
                    <span className="inline-block mt-1 text-xs font-bold text-indigo-700 bg-indigo-50 px-2.5 py-0.5 rounded-full border border-indigo-100">
                      {coach.team_name}
                    </span>
                  ) : (
                    <span className="inline-block mt-1 text-xs text-slate-400 italic">State Coach</span>
                  )}

                  {coach.district && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-500 font-medium mt-1.5">
                      <FaMapMarkerAlt className="text-rose-500" /> {coach.district}
                    </div>
                  )}
                </div>

                {/* Card Body: Disciplines & Achievements Count */}
                <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">
                      Disciplines Mentored
                    </span>
                    <div className="flex flex-wrap gap-1">
                      {coach.disciplines && coach.disciplines.length > 0 ? (
                        coach.disciplines.map((d, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-semibold border border-slate-200"
                          >
                            {d}
                          </span>
                        ))
                      ) : (
                        <span className="text-slate-400 text-xs italic">Para Sports General</span>
                      )}
                    </div>
                  </div>

                  {coach.achievements_count > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-600 font-bold bg-amber-50/80 border border-amber-200/80 px-2.5 py-1 rounded-xl">
                      <FaTrophy className="text-amber-500" />
                      <span>{coach.achievements_count} Verified Achievement{coach.achievements_count > 1 ? "s" : ""}</span>
                    </div>
                  )}

                  <button
                    onClick={() => openCoachModal(coach)}
                    className="w-full mt-2 py-2.5 px-4 bg-slate-900 hover:bg-indigo-900 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center justify-center gap-2 group-hover:shadow-md"
                  >
                    <FaAward /> View Profile & Achievements
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* ============================================================== */}
      {/* MODAL: COACH PROFILE & ACHIEVEMENTS */}
      {/* ============================================================== */}
      {selectedCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm p-4 overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-slate-200 overflow-hidden relative max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-900 text-white p-6 relative">
              <button
                onClick={closeCoachModal}
                className="absolute top-4 right-4 text-slate-300 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
              >
                <FaTimes size={18} />
              </button>

              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
                {selectedCoach.photo ? (
                  <img
                    src={resolvePhoto(selectedCoach.photo)}
                    alt={selectedCoach.name}
                    className="w-20 h-20 rounded-2xl object-cover border-2 border-amber-400 shadow-md"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-avatar.png";
                    }}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-2xl bg-white/10 flex items-center justify-center border-2 border-amber-400 shadow-md text-2xl font-black text-amber-300">
                    {selectedCoach.name?.charAt(0) || "C"}
                  </div>
                )}

                <div className="text-center sm:text-left">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-cyan-400/20 text-cyan-300 border border-cyan-400/30 mb-1">
                    Certified Para Sports Coach
                  </span>
                  <h2 className="text-2xl font-bold text-white leading-tight">
                    {selectedCoach.name}
                  </h2>
                  <p className="text-xs text-slate-300 mt-0.5">
                    {selectedCoach.team_name ? `Squad: ${selectedCoach.team_name} • ` : ""}
                    District: {selectedCoach.district || "Andhra Pradesh"}
                  </p>
                  {coachProfileData?.registration_id && (
                    <p className="text-[11px] text-amber-300 font-mono mt-0.5">
                      Reg ID: {coachProfileData.registration_id}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1">
              {/* Disciplines Strip */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                  <FaRunning className="text-indigo-600" /> Sports Disciplines
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {selectedCoach.disciplines && selectedCoach.disciplines.length > 0 ? (
                    selectedCoach.disciplines.map((d, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-indigo-50 text-indigo-900 border border-indigo-200 rounded-lg text-xs font-semibold"
                      >
                        {d}
                      </span>
                    ))
                  ) : (
                    <span className="text-slate-400 text-xs italic">Para Sports Association</span>
                  )}
                </div>
              </div>

              {/* Achievements Section */}
              <div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
                  <FaTrophy className="text-amber-500" /> Verified Achievements & Sporting Records
                </h4>

                {profileLoading ? (
                  <div className="py-8 text-center text-slate-400 text-xs">
                    <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    Loading achievements...
                  </div>
                ) : coachProfileData?.achievements && coachProfileData.achievements.length > 0 ? (
                  <div className="space-y-3">
                    {coachProfileData.achievements.map((ach) => (
                      <div
                        key={ach.id}
                        className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-100/80 transition"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2.5 rounded-xl text-white font-bold text-sm shadow-sm ${
                            ach.medal?.toLowerCase() === "gold"
                              ? "bg-amber-500"
                              : ach.medal?.toLowerCase() === "silver"
                              ? "bg-slate-400"
                              : ach.medal?.toLowerCase() === "bronze"
                              ? "bg-amber-700"
                              : "bg-indigo-600"
                          }`}>
                            <FaMedal className="text-lg" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                              {ach.event_name}
                              {ach.medal && (
                                <span className="px-2 py-0.5 rounded text-[10px] uppercase font-black bg-amber-100 text-amber-800 border border-amber-300">
                                  {ach.medal}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                              {ach.event_level && (
                                <span>Level: <strong>{ach.event_level}</strong></span>
                              )}
                              {ach.event_location && (
                                <span>• Location: {ach.event_location}</span>
                              )}
                              {ach.event_date && (
                                <span>• Date: {new Date(ach.event_date).toLocaleDateString()}</span>
                              )}
                            </div>
                            {ach.notes && (
                              <p className="text-xs text-slate-600 mt-1 italic">
                                "{ach.notes}"
                              </p>
                            )}
                          </div>
                        </div>

                        {ach.document_url && (
                          <a
                            href={ach.document_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg text-xs font-bold transition shadow-sm self-start sm:self-auto"
                          >
                            <FaFilePdf className="text-rose-500" /> Certificate <FaExternalLinkAlt className="text-[10px]" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-slate-500 text-xs">
                    <p className="font-semibold text-slate-700">Official Appointed Coach</p>
                    <p className="text-slate-400 mt-1">
                      No external past tournament achievement certificates uploaded yet. Appointed by Andhra Pradesh Para Sports Association for camp training.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 flex justify-end">
              <button
                onClick={closeCoachModal}
                className="px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CoachProfiles;
